// src/services/listManager.js
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, writeBatch, deleteField, collection, getDocs, FieldPath } from 'firebase/firestore';

function sanitize(input) {
  // Preserve user casing; just trim whitespace. Firestore allows spaces and case.
  return String(input || '').trim();
}

export async function createList(uid, listName) {
  const name = sanitize(listName);
  if (!uid) throw new Error('Not signed in');
  if (!name) throw new Error('List name required');
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  const user = snap.data() || {};
  const lists = user.lists || {};
  if (lists[name]) return { created: false, reason: 'exists' };
  // Use setDoc merge to avoid FieldPath when creating a new key that may include dots.
  await setDoc(userRef, { lists: { [name]: { listName: name, lastUpdated: Date.now() } } }, { merge: true });
  return { created: true, name };
}

export async function renameList(uid, oldName, newName) {
  const src = sanitize(oldName);
  const dst = sanitize(newName);
  if (!uid) throw new Error('Not signed in');
  if (!src || !dst) throw new Error('List names required');
  if (src === 'default') throw new Error('Cannot rename default');
  if (src === dst) return { renamed: false, reason: 'same' };

  const userRef = doc(db, 'users', uid);
  const user = (await getDoc(userRef)).data() || {};
  const lists = user.lists || {};
  if (!lists[src]) throw new Error('Source list missing');
  if (lists[dst]) throw new Error('Destination already exists');

  // Copy buyLists/{uid}/{src} -> {dst}
  const srcCol = collection(db, 'buyLists', uid, src);
  const batch = writeBatch(db);
  const srcDocs = await getDocs(srcCol);
  srcDocs.forEach((d) => {
    const dstRef = doc(db, 'buyLists', uid, dst, d.id);
    batch.set(dstRef, d.data());
  });
  // Remove old docs
  srcDocs.forEach((d) => {
    const delRef = doc(db, 'buyLists', uid, src, d.id);
    batch.delete(delRef);
  });

  // Update users/{uid}.lists using FieldPath to safely address keys with dots
  const dstPath = new FieldPath('lists', dst);
  const srcPath = new FieldPath('lists', src);
  batch.update(userRef, dstPath, { listName: dst, lastUpdated: Date.now() });
  batch.update(userRef, srcPath, deleteField());
  if (user.selectedList === src) {
    batch.update(userRef, { selectedList: dst });
  }
  await batch.commit();
  return { renamed: true, from: src, to: dst };
}

export async function deleteList(uid, listName) {
  const name = sanitize(listName);
  if (!uid) throw new Error('Not signed in');
  if (!name) throw new Error('List name required');
  if (name === 'default') throw new Error('Cannot delete default');

  // Delete subcollection buyLists/{uid}/{name}
  const colRef = collection(db, 'buyLists', uid, name);
  const docs = await getDocs(colRef);
  const batch = writeBatch(db);
  docs.forEach((d) => batch.delete(doc(db, 'buyLists', uid, name, d.id)));

  const userRef = doc(db, 'users', uid);
  const user = (await getDoc(userRef)).data() || {};
  const field = new FieldPath('lists', name);
  batch.update(userRef, field, deleteField());
  if (user.selectedList === name) batch.update(userRef, { selectedList: 'default' });
  await batch.commit();
  return { deleted: true, name };
}

export async function mergeList(uid, sourceListName, targetListName, deleteSource = false, boardMode = false) {
  const src = sanitize(sourceListName);
  const target = sanitize(targetListName);
  if (!uid) throw new Error('Not signed in');
  if (!src || !target) throw new Error('List names required');
  if (src === target) throw new Error('Cannot merge a list into itself');

  const collectionName = boardMode ? 'buyListsBoards' : 'buyLists';
  const listsField = boardMode ? 'listsBoards' : 'lists';

  const userRef = doc(db, 'users', uid);
  const user = (await getDoc(userRef)).data() || {};
  const lists = user[listsField] || {};

  if (!lists[src]) throw new Error('Source list does not exist');
  if (!lists[target]) throw new Error('Target list does not exist');

  // Get all items from source list
  const srcCol = collection(db, collectionName, uid, src);
  const srcDocs = await getDocs(srcCol);

  if (srcDocs.empty) {
    // Nothing to merge, optionally delete source
    if (deleteSource && src !== 'default') {
      const batch = writeBatch(db);
      const field = new FieldPath(listsField, src);
      batch.update(userRef, field, deleteField());
      if (user.selectedList === src || user.selectedListBoard === src) {
        const selField = boardMode ? 'selectedListBoard' : 'selectedList';
        batch.update(userRef, { [selField]: 'default' });
      }
      await batch.commit();
    }
    return { merged: true, itemsMerged: 0, sourceDeleted: deleteSource && src !== 'default' };
  }

  // Get existing items in target list to handle duplicates
  const targetCol = collection(db, collectionName, uid, target);
  const targetDocs = await getDocs(targetCol);
  const targetItems = {};
  targetDocs.forEach((d) => {
    targetItems[d.id] = d.data();
  });

  const batch = writeBatch(db);
  let itemsMerged = 0;

  srcDocs.forEach((d) => {
    const srcData = d.data();
    const itemId = d.id;
    const targetRef = doc(db, collectionName, uid, target, itemId);

    if (targetItems[itemId]) {
      // Item exists in target - add quantities
      const existingQty = Number(targetItems[itemId].qty) || 1;
      const srcQty = Number(srcData.qty) || 1;
      batch.update(targetRef, { qty: existingQty + srcQty, lastUpdated: Date.now() });
    } else {
      // Item doesn't exist in target - copy it over
      batch.set(targetRef, { ...srcData, lastUpdated: Date.now() });
    }
    itemsMerged++;
  });

  // Optionally delete source list and its items
  if (deleteSource && src !== 'default') {
    srcDocs.forEach((d) => {
      batch.delete(doc(db, collectionName, uid, src, d.id));
    });
    const field = new FieldPath(listsField, src);
    batch.update(userRef, field, deleteField());
    const selField = boardMode ? 'selectedListBoard' : 'selectedList';
    if (user[selField] === src) {
      batch.update(userRef, { [selField]: 'default' });
    }
  }

  await batch.commit();
  return { merged: true, itemsMerged, sourceDeleted: deleteSource && src !== 'default' };
}

/**
 * Merge two lists into a NEW list with a user-specified name.
 * The original lists are NOT deleted - user can delete them manually.
 * If the same item exists in both lists, quantities are combined.
 */
export async function mergeListsToNew(uid, listNameA, listNameB, newListName) {
  const listA = sanitize(listNameA);
  const listB = sanitize(listNameB);
  const newName = sanitize(newListName);

  if (!uid) throw new Error('Not signed in');
  if (!listA || !listB || !newName) throw new Error('All list names are required');
  if (listA === listB) throw new Error('Please select two different lists to merge');

  const userRef = doc(db, 'users', uid);
  const user = (await getDoc(userRef)).data() || {};
  const lists = user.lists || {};

  // Validate source lists exist
  if (!lists[listA]) throw new Error(`List "${listA}" does not exist`);
  if (!lists[listB]) throw new Error(`List "${listB}" does not exist`);

  // Validate new list name doesn't already exist
  if (lists[newName]) {
    return { merged: false, reason: 'exists' };
  }

  // Get all items from both source lists
  const colA = collection(db, 'buyLists', uid, listA);
  const colB = collection(db, 'buyLists', uid, listB);
  const [docsA, docsB] = await Promise.all([getDocs(colA), getDocs(colB)]);

  // Combine items, merging quantities for duplicates
  const mergedItems = {};

  docsA.forEach((d) => {
    const data = d.data();
    mergedItems[d.id] = { ...data, lastUpdated: Date.now() };
  });

  docsB.forEach((d) => {
    const data = d.data();
    if (mergedItems[d.id]) {
      // Item exists in both lists - combine quantities (field is 'total', not 'qty')
      const existingTotal = parseFloat(String(mergedItems[d.id].total)) || 0;
      const newTotal = parseFloat(String(data.total)) || 0;
      mergedItems[d.id].total = existingTotal + newTotal;
      mergedItems[d.id].lastUpdated = Date.now();
    } else {
      mergedItems[d.id] = { ...data, lastUpdated: Date.now() };
    }
  });

  const batch = writeBatch(db);

  // Create new list in users/{uid}.lists and set it as selected
  await setDoc(userRef, {
    lists: {
      [newName]: { listName: newName, lastUpdated: Date.now() }
    },
    selectedList: newName
  }, { merge: true });

  // Write all merged items to the new list
  for (const [itemId, itemData] of Object.entries(mergedItems)) {
    const itemRef = doc(db, 'buyLists', uid, newName, itemId);
    batch.set(itemRef, itemData);
  }

  await batch.commit();

  return { merged: true, newListName: newName, itemCount: Object.keys(mergedItems).length };
}

